// @givmo/app-charity — PORTAL ENTRY gate (CPA-WEB10, D-CP-36; converted onto the console by CPA-WEB40).
//
// Owns the one decision every authenticated caller hits before anything else: which portal are they
// in? It resolves from the two self-scoped reads the app already makes — `charityAdminListMyMemberships`
// (via the active-org context) and `charityAdminListMyCharityClaims` — and renders the CHROME to match.
//
//   ONBOARDING — no membership, no claim awaiting review.
//     The claim flow itself, full-screen in <OnboardingShell>. Someone with no organization has
//     exactly one thing to do, so a sidebar of Home / Donations / Profile links would be dead ends
//     dressed up as a product. Every org-scoped path redirects to the claim entry, so the wizard IS
//     the screen rather than something to go and find.
//     ⚠ THIS IS ALSO WHERE THE CLASS-MISMATCH FORWARD HOOKS (contract §Forward). The onboarding entry
//     is a POSITIVE no-access resolution — the memberships read SUCCEEDED and carried none — which is
//     what makes forwarding safe: a failed read never reaches it, so a timeout can never bounce a real
//     admin off their own console. Someone who administers a COMPANY and signed in here is one host
//     away; the forward sends them to that console's root when the backend serves `/org/memberships`
//     and this build was given the company console's origin. Both are absent today, so every branch
//     resolves to "stay" — the forward is dark by construction, with no flag of its own.
//
//   CLAIM-PENDING — no membership, but a claim is awaiting review.
//     The class door: the onboarding chrome around the claim's status card at `/`
//     (`features/claim/claim-pending-door.tsx`). The console is never entered before approval;
//     org-scoped deep links replace-redirect to the door.
//
//   PORTAL — a membership exists, or the memberships read failed.
//     The normal <AppShell>, unchanged. A FAILED read deliberately keeps the full portal: an admin
//     whose memberships call errored is not an unclaimed user and must never be re-onboarded — the
//     grid shows its retryable error instead.
//
// ★ NO state renders a Claim nav group (D-CP-36, 2026-08-10). Claiming is a once-ever act for the
// typical admin, so a permanent left-nav section for it is clutter from the moment it is done; the
// rare second claim is an action inside the org switcher. What changed is the NAVIGATION — the routes
// are untouched. `/claim`, `/claims`, `/invites/accept` and `/support` still resolve in either chrome,
// so deep links, the wizard, and the claim-detail view the status card points at all keep working.

import { useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { charityAdminListMyCharityClaimsOptions } from '@givmo/api-client/query';
import { Skeleton, testIdProp } from '@givmo/design-system';
import { useMismatchForward } from '@givmo/org-console';
import { AppShell } from '../components/app-shell';
import { OnboardingShell } from '../components/onboarding-shell';
import { ClaimPendingDoor } from '../features/claim/claim-pending-door';
import { extractClaims, findPendingClaim } from '../features/claim/claim-decode';
import { CLAIM_ROUTES } from '../features/claim/claim-routes';
import { mismatchForwardDestinations } from '../lib/env';
import { useActiveOrg } from './active-org-context';

// `/support` earns its place here for the same reason as the claim routes (EP-FIX7): the org-search
// no-match guidance and the claim wizard's not-claimable panel both link to it, and both are shown
// precisely to callers who have NO membership yet. Gating it would bounce them back to /claim — the
// exact dead end they were trying to escape.
const CLAIM_FIRST_PATHS = ['/claim', '/claims', '/invites/accept', '/support'] as const;

/** Claim, invite, and support routes remain reachable before the caller has an organization membership. */
export function isClaimFirstPath(pathname: string): boolean {
  return CLAIM_FIRST_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

/** Which portal an authenticated caller is in. */
export type PortalEntry = 'resolving' | 'onboarding' | 'claim-pending' | 'portal';

export function PortalEntryGate({ children }: { children: ReactNode }) {
  const { membershipsState } = useActiveOrg();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const noOrgYet = membershipsState === 'loaded-empty';

  // Read only while the caller has no organization — the one state whose CHROME depends on it. A
  // claimed admin's own pending claim on a SECOND org is read by the app grid instead; both use the
  // same query key, so React Query still issues a single request wherever they overlap.
  const claimsQuery = useQuery({
    ...charityAdminListMyCharityClaimsOptions(),
    select: extractClaims,
    enabled: noOrgYet,
  });

  const pendingClaim = findPendingClaim(claimsQuery.data ?? []);
  const entry: PortalEntry = !noOrgYet
    ? 'portal'
    : claimsQuery.isPending
      ? 'resolving'
      : // A FAILED claims read falls to onboarding, not to the shell: the claim flow is the only
        // actionable surface for a caller with no organization, and re-submitting an existing claim
        // is answered by the backend. A shell we cannot fill is the worse of the two wrong answers.
        pendingClaim !== null
        ? 'claim-pending'
        : 'onboarding';

  if (entry === 'resolving') {
    // Resolve inside the ONBOARDING frame, not the shell: this window is only ever entered by a
    // caller we already know has no organization, so the shell is the one chrome that is certainly
    // wrong for them, and both outcomes from here paint into a settled layout.
    //
    // ⚠ This does NOT make cold load flash-free, and the comment must not pretend otherwise. While
    // the MEMBERSHIPS read is still pending the state is `portal` (see above), so a first-time admin
    // sees the SaaS shell for that read's duration before it is replaced by this frame — visible in
    // the preview harness, and pre-existing (the old gate flashed the full nav then narrowed it).
    // There is no free fix: on a cold load nothing is known until that read answers, and whichever
    // chrome is guessed is wrong for someone. Guessing "shell" is the right guess because a claimed
    // admin is the overwhelmingly common caller — and `app.auth-lifecycle.test.tsx`'s LOW-6 case
    // pins the shell's account identity as NOT hostage to the memberships read, which a
    // splash-until-resolved would break.
    return (
      <OnboardingShell>
        <EntryResolving />
      </OnboardingShell>
    );
  }

  if (entry === 'onboarding') {
    return (
      <OnboardingShell>
        <MismatchForward pathname={pathname} />
        {isClaimFirstPath(pathname) ? children : <RedirectTo to={CLAIM_ROUTES.search} />}
      </OnboardingShell>
    );
  }
  // ⚠ A CLAIM IN REVIEW STAYS AT THE CLASS DOOR (Jonathan, 2026-09-02). The console — its shell, its
  // grid, its sidebar — is for a resolved organization. The door is the onboarding chrome around the
  // claim's status card, at `/`; the claim-first paths stay reachable in that chrome, and every other
  // path replace-redirects to the door rather than to `/apps`, which never shows the pending card.
  if (entry === 'claim-pending' && pendingClaim !== null) {
    return (
      <OnboardingShell>
        {isClaimFirstPath(pathname) ? (
          children
        ) : pathname === '/' ? (
          <ClaimPendingDoor claim={pendingClaim} />
        ) : (
          <RedirectTo to="/" />
        )}
      </OnboardingShell>
    );
  }

  return <AppShell>{children}</AppShell>;
}

/**
 * The class-mismatch forward, mounted ONLY in the positive no-access branch (see the header).
 *
 * A leaf that renders nothing: the onboarding screen keeps rendering while the read resolves — the
 * fail-toward-staying posture — and a forward, when it happens, replaces the document. The read's
 * own dark gate is the reveal switch: `GET /org/memberships` does not exist on the backend yet, so
 * its route-miss 404 is "no answer" and nobody moves.
 */
/**
 * The AUTO form of the class-mismatch forward: for a no-membership identity that is NOT on a
 * claim-first path. On `/claim*`, `/invites/accept` and `/support` the LINK form renders instead
 * (`org-search-page.tsx`), never a redirect out from under a person who is reading the page.
 *
 * ⚠ DISABLED MEANS NO DESTINATIONS, NOT JUST NO FETCH. The link form reads the SAME query key, so
 * its answer is in the cache — and a hook that merely stops fetching still sees cached memberships
 * and would forward off them. Handing it an empty destination map is what makes it inert. Measured:
 * the claim door at 390 navigated to the company console mid-capture before this line existed.
 */
export function mismatchForwardOptions(pathname: string): {
  enabled: boolean;
  destinations: Record<string, string | null>;
} {
  const enabled = !isClaimFirstPath(pathname);
  return { enabled, destinations: enabled ? mismatchForwardDestinations() : {} };
}

function MismatchForward({ pathname }: { pathname: string }) {
  useMismatchForward(mismatchForwardOptions(pathname));
  return null;
}

/**
 * Send an org-scoped deep link to the one destination that can answer it in this state.
 *
 * Rendering `null` while the navigation lands is deliberate: the alternative is mounting an org page
 * that has no org, which paints its own fail-closed error on the way past.
 */
function RedirectTo({ to }: { to: string }) {
  const navigate = useNavigate();
  useEffect(() => {
    void navigate({ to, replace: true });
  }, [navigate, to]);
  return null;
}

/** The gap between "you have no organization" and knowing whether a claim is already in review. */
function EntryResolving() {
  return (
    <div
      className="mx-auto flex w-full max-w-[900px] flex-col gap-16 px-24 py-24"
      {...testIdProp('portal-entry-resolving')}
    >
      <Skeleton className="h-20 w-1/3" />
      <Skeleton className="h-14 w-2/3" />
      <Skeleton className="h-[120px] w-full" />
    </div>
  );
}
